import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User, X } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { api, Client } from '../../lib/api';

interface ClientSearchAutocompleteProps {
  value: string;
  onChange: (clientId: string, clientName?: string) => void;
  onClientSelect?: (client: Client | null) => void;
  onClearError?: () => void;
  error?: string;
  placeholder?: string;
  label?: string;
  isEmployee?: boolean;
  onCreateRequest?: (name: string) => void;
  createLabel?: string;
}

export function ClientSearchAutocomplete({
  value,
  onChange,
  onClientSelect,
  onClearError,
  error,
  placeholder,
  label,
  isEmployee,
  onCreateRequest,
  createLabel,
}: ClientSearchAutocompleteProps) {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();

  const [searchQuery, setSearchQuery] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch clients based on search query
  const fetchClients = useCallback(async (query: string) => {
    if (!currentEntity?.id) return;

    setIsLoading(true);
    try {
      const result = await api.clients.list(currentEntity.id, {
        search: query?.trim() ? query.trim().toLowerCase() : undefined,
        limit: 10,
        ...(isEmployee !== undefined ? { isEmployee } : {}),
      });
      setClients(result.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentEntity?.id, isEmployee]);

  // Debounced search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setDisplayValue(query);
    setIsOpen(true);
    onClearError?.();

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new debounce timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchClients(query);
    }, 300);

    // If user is typing, clear the selection (they're searching for something new)
    if (selectedClient && query !== selectedClient.name && query !== selectedClient.uniqueId) {
      setSelectedClient(null);
      onChange('');
    }
  };

  // Handle client selection
  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setDisplayValue(client.name || client.uniqueId);
    setSearchQuery('');
    setIsOpen(false);
    // Pass client name (not UUID) - the server will resolve the name to UUID
    onChange(client.name, client.name);
    onClientSelect?.(client);
    onClearError?.();
  };

  // Handle clearing selection
  const handleClear = () => {
    setSelectedClient(null);
    setDisplayValue('');
    setSearchQuery('');
    onChange('');
    onClientSelect?.(null);
    inputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initial fetch on mount or when isEmployee filter changes
  useEffect(() => {
    if (currentEntity?.id) {
      fetchClients('');
    }
  }, [currentEntity?.id, fetchClients]);

  // Sync display value if external value changes
  useEffect(() => {
    if (!value && selectedClient) {
      setSelectedClient(null);
      setDisplayValue('');
    }
  }, [value, selectedClient]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleInputFocus = () => {
    setIsOpen(true);
    if (clients.length === 0 && !isLoading) {
      fetchClients(searchQuery);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
        {label || t('cashier.createOperation.client', 'CLIENT')}
      </label>

      <div ref={containerRef} className="relative">
        {/* Input Field */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none"
          />
          <input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleSearchChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            className={`
              w-full py-3 pl-10 pr-10 border rounded-lg text-sm text-foreground bg-surface
              focus:outline-none focus:border-ctp-teal
              placeholder:text-foreground-muted
              ${error ? 'border-ctp-red' : 'border-border'}
            `}
            placeholder={placeholder || t('cashier.createOperation.searchPlaceholder', 'Search clients...')}
            autoComplete="off"
          />

          {/* Clear button */}
          {displayValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-foreground-muted text-center">
                {t('common.loading', 'Loading...')}
              </div>
            ) : clients.length === 0 ? (
              <div className="px-4 py-3 text-sm text-foreground-muted text-center">
                {searchQuery
                  ? t('cashier.createOperation.noClientsFound', 'No clients found')
                  : t('cashier.createOperation.noClients', 'No clients yet')}
              </div>
            ) : (
              <ul className="py-1">
                {clients.map((client) => (
                  <li key={client.id}>
                    <button
                      type="button"
                      onClick={() => handleSelectClient(client)}
                      className={`
                        w-full px-4 py-2.5 flex items-center gap-3 text-left
                        hover:bg-surface-hover transition-colors
                        ${selectedClient?.id === client.id ? 'bg-surface-hover' : ''}
                      `}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-ctp-teal/10 flex items-center justify-center">
                        <User size={14} className="text-ctp-teal" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate flex items-center gap-2">
                          <span className="truncate">{client.name || client.uniqueId}</span>
                          {client.isEmployee && (
                            <span className="text-[10px] uppercase tracking-wide bg-ctp-teal/15 text-ctp-teal rounded px-1.5 py-0.5">
                              {t('cashier.clients.employee', 'Employee')}
                            </span>
                          )}
                        </div>
                        {client.name && (
                          <div className="text-xs text-foreground-muted truncate">
                            ID: {client.uniqueId}
                          </div>
                        )}
                        {client.transactionCount !== undefined && (
                          <div className="text-xs text-foreground-muted">
                            {client.transactionCount} {t('cashier.clients.transactions', 'transactions')}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Option to use typed value as new client */}
            {searchQuery && !clients.some(c => c.name === searchQuery || c.uniqueId === searchQuery) && (
              <div className="border-t border-border">
                <button
                  type="button"
                  onClick={() => {
                    if (onCreateRequest) {
                      setIsOpen(false);
                      onCreateRequest(searchQuery);
                    } else {
                      onChange(searchQuery, searchQuery);
                      setDisplayValue(searchQuery);
                      setSelectedClient(null);
                      setIsOpen(false);
                    }
                  }}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-surface-hover transition-colors"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-ctp-green/10 flex items-center justify-center">
                    <User size={14} className="text-ctp-green" />
                  </div>
                  <div className="text-sm text-foreground">
                    {createLabel || t('cashier.createOperation.useAsNewClient', 'Use')}{' '}
                    <span className="font-medium">"{searchQuery}"</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <span className="text-xs text-ctp-red">{error}</span>
      )}
    </div>
  );
}
